import { useState, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { T } from '../lib/tokens'
import { useChatMemos, useChatModels } from '../lib/hooks'
import { itemChat, claudeChat, createChatMemo, chatTitle, itemChatToMemo, modelLabel } from '../lib/api'
import { supabase } from '../lib/supabase'
import { Sidebar } from '../components/Sidebar'
import { Topbar } from '../components/Topbar'
import { GeminiDrawer, useGeminiDrawer } from '../components/GeminiDrawer'
import { CopyButton, LoadingSpinner, MarkdownLite } from '../components/ui'

// key … React用のローカル識別子。常にある。
// id  … ml_item_chats の行ID。通信エラー表示など「DBに書かれなかった行」には無い。
//        削除時はこれの有無で「DBも消す」か「画面から消すだけ」かが決まる。
interface Msg {
  key: string
  id?: string
  role: 'user' | 'assistant'
  content: string
  searched?: boolean
  model?: string | null
  ts: string
}

// 削除を取り消すための退避。deadline までに undo されなければ復元不可になる。
interface UndoState { itemId: string; snapshot: Msg[]; rows: Msg[] }

function newKey(): string {
  return crypto.randomUUID()
}

// 「1つの発言」を含む会話ターン（user + それに続く assistant 群）を返す。
// 途中削除でも履歴が user/assistant の対で保たれるよう、常にターン単位で消す。
function turnOf(msgs: Msg[], index: number): Msg[] {
  let start = index
  while (start > 0 && msgs[start].role !== 'user') start--
  let end = start + 1
  while (end < msgs.length && msgs[end].role === 'assistant') end++
  return msgs.slice(start, end)
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleString('ja-JP', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
}

// ブラウザで Gemini チャットに話しかける感覚の画面。
// 1スレッド = kind='chat' のメモ。会話は ml_item_chats に保存され、メインのメモ一覧にも並ぶ。
export function ChatPage() {
  const qc = useQueryClient()
  const { open: geminiOpen, setOpen: setGeminiOpen } = useGeminiDrawer()
  const { data: chats = [], isLoading } = useChatMemos()

  const [selected, setSelected] = useState<string | null>(null)
  const [messages, setMessages] = useState<Msg[]>([])
  const [loadingMsg, setLoadingMsg] = useState(false)
  const [input, setInput] = useState('')
  // Gemini の選択肢は Worker から取得する。未選択（null）の間は一覧の先頭を使う。
  // 一覧を取得できないときは model が空になり、モデル指定なしで送る（Worker の既定で回答）。
  // Claude の選択肢も Worker から取得する（API キーが未設定の間は空で返り、選択肢に出ない）。
  const { data: chatModelList, isError: chatModelsError } = useChatModels()
  const chatModels = chatModelList?.models
  const claudeModels = chatModelList?.claude ?? []
  const [picked, setPicked] = useState<string | null>(null)
  const model = picked ?? chatModels?.[0]?.value ?? ''
  const isClaude = claudeModels.some(m => m.value === model)
  const [useMemos, setUseMemos] = useState(false) // 「メモ参照」トグル（既定OFF）
  const [sending, setSending] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveMsg, setSaveMsg] = useState<string | null>(null)
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState('')
  const [undo, setUndo] = useState<UndoState | null>(null)

  const bottomRef = useRef<HTMLDivElement>(null)
  const undoTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])
  useEffect(() => () => { if (undoTimer.current) clearTimeout(undoTimer.current) }, [])

  // 会話ターンを削除する。DBの行は即時 DELETE し、5秒だけ復元用に保持する。
  // 「元に戻す」は id / created_at ごと INSERT し直すので、並び順もそのまま戻る。
  async function deleteTurn(index: number) {
    if (!selected) return
    const snapshot = messages
    const rows = turnOf(snapshot, index)
    const ids = rows.map(m => m.id).filter((v): v is string => !!v)

    setMessages(prev => prev.filter(m => !rows.includes(m)))
    // ids が空＝DBに書かれなかった行（通信エラー表示など）だけのターン。画面から消して終わり。
    if (ids.length) await supabase.from('ml_item_chats').delete().in('id', ids)

    if (undoTimer.current) clearTimeout(undoTimer.current)
    setUndo({ itemId: selected, snapshot, rows })
    undoTimer.current = setTimeout(() => setUndo(null), 5000)
  }

  async function undoDelete() {
    if (!undo) return
    if (undoTimer.current) clearTimeout(undoTimer.current)
    const { itemId, snapshot, rows } = undo
    setUndo(null)

    const restore = rows.filter(m => m.id).map(m => ({
      id: m.id, item_id: itemId, role: m.role, content: m.content,
      searched: !!m.searched, model: m.model ?? null, created_at: m.ts,
    }))
    if (restore.length) await supabase.from('ml_item_chats').insert(restore)
    // 別スレッドに移っていたら画面は触らない（DBだけ戻す）。
    if (selected === itemId) setMessages(snapshot)
  }

  async function openChat(id: string) {
    setSelected(id)
    setEditingTitle(false)
    setLoadingMsg(true)
    setMessages(await fetchMessages(id))
    setLoadingMsg(false)
  }

  async function fetchMessages(id: string): Promise<Msg[]> {
    const { data } = await supabase
      .from('ml_item_chats').select('*').eq('item_id', id).order('created_at', { ascending: true })
    return (data ?? []).map((r: any) => ({
      key: newKey(), id: r.id, role: r.role, content: r.content,
      searched: r.searched, model: r.model, ts: r.created_at,
    }))
  }

  function newChat() {
    setSelected(null)
    setMessages([])
    setInput('')
    setEditingTitle(false)
    setSaveMsg(null)
    setUndo(null)
  }

  async function saveAsMemo() {
    if (!selected || saving) return
    setSaving(true)
    setSaveMsg('💾 メモとして保存中…')
    try {
      const r = await itemChatToMemo(selected)
      if (r.ok && r.item_id) {
        setSaveMsg(`✓ メモとして保存しました（カテゴリ: ${r.category ?? '?'}）`)
      } else {
        setSaveMsg('⚠️ 保存に失敗しました')
      }
    } catch {
      setSaveMsg('⚠️ 保存に失敗しました')
    } finally {
      setSaving(false)
      setTimeout(() => setSaveMsg(null), 5000)
    }
  }

  async function saveTitle() {
    const t = titleDraft.trim()
    setEditingTitle(false)
    if (!selected || !t) return
    await supabase.from('ml_items').update({ title: t }).eq('id', selected)
    qc.invalidateQueries({ queryKey: ['chat-memos'] })
  }

  function attachRowId(key: string, id: string) {
    setMessages(prev => prev.map(m => (m.key === key ? { ...m, id } : m)))
  }

  async function send() {
    const q = input.trim()
    if (!q || sending) return
    setSending(true)
    setInput('')

    // 新規スレッドなら chat メモを先に作成
    let itemId = selected
    let freshlyCreated = false
    if (!itemId) {
      try {
        const { item_id } = await createChatMemo(q)
        itemId = item_id
        freshlyCreated = true
        setSelected(item_id)
      } catch {
        setMessages(prev => [...prev, { key: newKey(), role: 'assistant', content: '⚠️ チャットの作成に失敗しました。', ts: new Date().toISOString() }])
        setSending(false)
        return
      }
    }

    const history = messages.slice(-200).map(({ role, content }) => ({ role, content }))
    const userKey = newKey()
    setMessages(prev => [...prev, { key: userKey, role: 'user', content: q, ts: new Date().toISOString() }])
    // ユーザー発言を保存（失敗してもチャットは継続）。返ってきた行IDを画面側に紐づけて、
    // リロード前でも「この発言を削除」できるようにする。
    supabase.from('ml_item_chats')
      .insert({ item_id: itemId, role: 'user', content: q })
      .select('id').single()
      .then(({ data }) => { if (data) attachRowId(userKey, data.id) })

    try {
      // Claude はメモ参照なしの素のチャット専用なので、別エンドポイントへ振る。
      const r = isClaude
        ? await claudeChat(q, history, model)
        : await itemChat(itemId, q, history, model || undefined, useMemos)
      const { answer, searched } = r
      // Worker が返した実モデル名（例: 'Flash Lite 3.5'）をそのまま保存・表示する。無ければ選択キー。
      const usedModel = (r as { model?: string }).model ?? (model || null)
      const asstKey = newKey()
      setMessages(prev => [...prev, { key: asstKey, role: 'assistant', content: answer, searched, model: usedModel, ts: new Date().toISOString() }])
      supabase.from('ml_item_chats')
        .insert({ item_id: itemId, role: 'assistant', content: answer, searched: !!searched, model: usedModel })
        .select('id').single()
        .then(({ data }) => { if (data) attachRowId(asstKey, data.id) })
      if (freshlyCreated) {
        qc.invalidateQueries({ queryKey: ['chat-memos'] })
        chatTitle(q).then(({ title }) => {
          if (title) {
            supabase.from('ml_items').update({ title }).eq('id', itemId).then(() => {
              qc.invalidateQueries({ queryKey: ['chat-memos'] })
            })
          }
        }).catch(() => {})
      }
    } catch {
      setMessages(prev => [...prev, { key: newKey(), role: 'assistant', content: '⚠️ 通信エラーが発生しました。', ts: new Date().toISOString() }])
    } finally {
      setSending(false)
    }
  }

  async function deleteChat(id: string, e: React.MouseEvent) {
    e.stopPropagation()
    if (!confirm('このチャットを削除しますか？')) return
    await supabase.from('ml_items').delete().eq('id', id) // ml_item_chats は CASCADE 削除
    if (selected === id) newChat()
    qc.invalidateQueries({ queryKey: ['chat-memos'] })
  }

  const current = chats.find(c => c.id === selected)

  // 表示中の会話を全文 Markdown で書き出す（件数・長さ無制限）
  function exportMarkdown() {
    if (!current || messages.length === 0) return
    const title = current.title || '無題のチャット'
    const now = new Date()
    const lines: string[] = [`# ${title}`, '', `出力: ${fmtTime(now.toISOString())}`, '', '---', '']
    for (const m of messages) {
      const who = m.role === 'user' ? 'あなた' : 'Gemini'
      const meta = m.role === 'assistant'
        ? [modelLabel(m.model), m.searched ? 'ウェブ検索あり' : ''].filter(Boolean).join('・')
        : ''
      lines.push(`## ${who}（${fmtTime(m.ts)}${meta ? '・' + meta : ''}）`, '', m.content, '')
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const d = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`
    const safe = title.replace(/[\\/:*?"<>|\n\r]+/g, '_').slice(0, 50).trim() || 'chat'
    a.download = `${safe}_${d}.md`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div style={{ display: 'flex', height: '100dvh', background: T.bg, color: T.text, fontFamily: T.font, overflow: 'hidden' }}>
      <Sidebar />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Topbar subtitle="チャット" onGemini={() => setGeminiOpen(true)} />

        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

          {/* 左: スレッド一覧 */}
          <div style={{ width: 300, flexShrink: 0, borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: 12, borderBottom: `1px solid ${T.border}` }}>
              {/* 選択中スレッド（accentSoft / accentBorder）と同系色のまま、
                  背景と枠を一段強め・文字を accent にして「操作」と「状態」を描き分ける。 */}
              <button onClick={newChat} style={{
                width: '100%', background: 'oklch(0.72 0.16 258 / 0.22)',
                border: '1px solid oklch(0.72 0.16 258 / 0.45)', borderRadius: 6,
                color: T.accent, padding: '9px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: T.fontMono,
              }}>＋ 新規チャット</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {isLoading && <LoadingSpinner />}
              {!isLoading && chats.length === 0 && (
                <div style={{ fontSize: 12, color: T.textMute, padding: '12px 4px', lineHeight: 1.7 }}>
                  まだチャットはありません。「＋ 新規チャット」か Discord の /ask で始められます。
                </div>
              )}
              {chats.map(c => (
                <div key={c.id} onClick={() => openChat(c.id)} style={{
                  background: c.id === selected ? T.accentSoft : T.bgRaised,
                  border: `1px solid ${c.id === selected ? T.accentBorder : T.border}`,
                  borderRadius: 6, padding: '10px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <span style={{ flex: 1, fontSize: 13, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.title || '新しいチャット'}
                  </span>
                  <span style={{ fontSize: 10, color: T.textMute, fontFamily: T.fontMono }}>{fmtTime(c.created_at)}</span>
                  <button onClick={(e) => deleteChat(c.id, e)} title="削除" style={{
                    background: 'transparent', border: 'none', color: T.textMute, cursor: 'pointer', fontSize: 12, padding: 0,
                  }}>🗑</button>
                </div>
              ))}
            </div>
          </div>

          {/* 右: 会話 + 入力（position: relative は削除取り消しトーストの基準） */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
            {selected && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px', borderBottom: `1px solid ${T.border}` }}>
                {editingTitle ? (
                  <input
                    value={titleDraft} autoFocus
                    onChange={e => setTitleDraft(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') setEditingTitle(false) }}
                    onBlur={saveTitle}
                    style={{ flex: 1, fontSize: 14, fontWeight: 600, color: T.text, background: T.bgRaised, border: `1px solid ${T.borderHi}`, borderRadius: 6, padding: '4px 8px', fontFamily: 'inherit', outline: 'none' }}
                  />
                ) : (
                  <span
                    onClick={() => { setTitleDraft(current?.title ?? ''); setEditingTitle(true) }}
                    title="クリックしてタイトルを編集"
                    style={{ flex: 1, fontSize: 14, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'text' }}
                  >
                    {current?.title || '無題のチャット'}
                  </span>
                )}
                <button onClick={exportMarkdown} disabled={messages.length === 0} title="この会話を Markdown で保存" style={{
                  background: 'transparent', border: `1px solid ${T.border}`, color: T.textDim,
                  borderRadius: 5, padding: '5px 10px', fontSize: 11, cursor: messages.length === 0 ? 'default' : 'pointer', fontFamily: T.fontMono,
                }}>MD書き出し</button>
                {messages.some(m => m.role === 'assistant') && (
                  <button onClick={saveAsMemo} disabled={saving} title="この会話をメモとして保存（自動分類）" style={{
                    background: 'transparent', border: `1px solid ${T.border}`,
                    color: saving ? T.textMute : T.ok,
                    borderRadius: 5, padding: '5px 10px', fontSize: 11, cursor: saving ? 'default' : 'pointer', fontFamily: T.fontMono,
                  }}>{saving ? '💾…' : '📝 メモ化'}</button>
                )}
                {saveMsg && (
                  <span style={{ fontSize: 11, fontFamily: T.fontMono, color: saveMsg.startsWith('✓') ? T.ok : T.textDim }}>
                    {saveMsg}
                  </span>
                )}
              </div>
            )}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
              {loadingMsg && <LoadingSpinner />}
              {!loadingMsg && messages.length === 0 && (
                <div style={{ maxWidth: 780, margin: '0 auto', color: T.textMute, fontSize: 13, lineHeight: 1.8, paddingTop: 40, textAlign: 'center' }}>
                  Gemini に話しかけてください。ソースは不要です。<br />
                  会話は自動でメモとして保存され、メインのメモ一覧にも表示されます。
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 780, margin: '0 auto' }}>
                {messages.map((m, i) => (
                  <div key={m.key} style={{ display: 'flex', flexDirection: 'column', alignItems: m.role === 'user' ? 'flex-end' : 'flex-start', gap: 4 }}>
                    <div style={{
                      maxWidth: '90%',
                      background: m.role === 'user' ? T.accentSoft : T.bgRaised,
                      border: `1px solid ${m.role === 'user' ? T.accentBorder : T.border}`,
                      borderRadius: 8, padding: '10px 13px', fontSize: 13, color: T.text, lineHeight: 1.7,
                      whiteSpace: m.role === 'user' ? 'pre-wrap' : 'normal', wordBreak: 'break-word',
                    }}>
                      {m.role === 'assistant' ? <MarkdownLite text={m.content} /> : m.content}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10, fontFamily: T.fontMono, color: T.textMute }}>
                      <span>{fmtTime(m.ts)}</span>
                      {m.role === 'assistant' && m.model && <span>{modelLabel(m.model)}</span>}
                      {m.role === 'assistant' && m.searched && <span>🔍 ウェブ検索あり</span>}
                      <CopyButton text={m.content} />
                      <button
                        onClick={() => deleteTurn(i)}
                        title="このやり取り（質問と回答）を削除"
                        style={{ background: 'transparent', border: 'none', padding: 0, color: T.textMute, fontSize: 10, fontFamily: T.fontMono, cursor: 'pointer', lineHeight: 1 }}
                      >🗑 削除</button>
                    </div>
                  </div>
                ))}
                {sending && <div style={{ fontSize: 12, color: T.textMute, fontFamily: T.fontMono }}>✦ 考え中…</div>}
                <div ref={bottomRef} />
              </div>
            </div>

            {/* 削除の取り消し（5秒） */}
            {undo && (
              <div style={{
                position: 'absolute', bottom: 96, left: '50%', transform: 'translateX(-50%)',
                display: 'flex', alignItems: 'center', gap: 12,
                background: T.bgRaised, border: `1px solid ${T.borderHi}`, borderRadius: 6,
                padding: '8px 14px', fontSize: 12, color: T.text, zIndex: 10,
              }}>
                <span>やり取りを削除しました</span>
                <button onClick={undoDelete} style={{
                  background: 'transparent', border: 'none', padding: 0,
                  color: T.accent, fontSize: 12, fontFamily: T.fontMono, cursor: 'pointer',
                }}>元に戻す</button>
              </div>
            )}

            {/* 入力 + モデル選択 */}
            <div style={{ borderTop: `1px solid ${T.border}`, padding: 16 }}>
              {/* Claude はメモを参照しない経路なので、トグル自体を出さない
                  （出すと「ONにしたのに参照されない」という誤解になる）。 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: 780, margin: '0 auto 8px' }}>
                {isClaude ? (
                  <span style={{ fontSize: 11, color: T.textMute, fontFamily: T.fontMono }}>
                    メモ参照なし・ウェブ検索あり
                  </span>
                ) : (
                  <button
                    onClick={() => setUseMemos(v => !v)}
                    title="保存メモ全体（要約）を参照して回答する"
                    style={{
                      background: useMemos ? T.bgActive : 'transparent',
                      border: `1px solid ${useMemos ? T.borderHi : T.border}`,
                      color: useMemos ? T.text : T.textDim,
                      borderRadius: 5, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontFamily: T.fontMono,
                    }}
                  >{useMemos ? 'メモ参照 ON' : 'メモ参照 OFF'}</button>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: 780, margin: '0 auto', background: T.bgRaised, border: `1px solid ${T.borderHi}`, borderRadius: 8, padding: '8px 12px' }}>
                <select
                  value={model}
                  onChange={e => setPicked(e.target.value || null)}
                  title="モデルを選択"
                  style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 5, color: T.text, fontSize: 12, fontFamily: T.fontMono, padding: '5px 6px', cursor: 'pointer', outline: 'none' }}
                >
                  {!chatModels && <option value="">{chatModelsError ? '⚠️ モデル一覧を取得できません' : '読み込み中…'}</option>}
                  {[...(chatModels ?? []), ...claudeModels].map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
                <textarea
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }}
                  placeholder="メッセージを入力…"
                  disabled={sending}
                  rows={1}
                  style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: T.text, fontSize: 13, fontFamily: T.font, resize: 'vertical', minHeight: 20, maxHeight: 320, lineHeight: 1.6 }}
                />
                <button onClick={send} disabled={!input.trim() || sending} style={{
                  background: input.trim() ? T.accent : T.bgHover, border: 'none', borderRadius: 5,
                  color: input.trim() ? '#0a0b0e' : T.textMute, padding: '6px 12px', fontSize: 12, fontWeight: 600,
                  cursor: input.trim() ? 'pointer' : 'default', fontFamily: T.fontMono,
                }}>{sending ? '送信中…' : '↵ 送信'}</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <GeminiDrawer open={geminiOpen} onClose={() => setGeminiOpen(false)} />
    </div>
  )
}
